/**
 * @file usePlayerDeepLink.ts
 * @description Opens the player preview drawer from a `?playerId=<uuid>`
 *   deep-link param, so a pipeline newsfeed click lands on "board X with
 *   player Y's drawer open" instead of just the board (SE-7121).
 * @module features/player-kanban/hooks/usePlayerDeepLink
 */

import { useEffect, useRef, useState } from "react";

import { useReactiveVar } from "@apollo/client";
import { useLocalSearchParams } from "expo-router";

import { readInitialRouteParam } from "@/utils/persistedRouteParams";

import {
  PLAYER_ID_PARAM,
  PLAYER_KANBAN_ROUTE_KEY,
} from "../constants/routeParams";
import {
  consumePlayerDeepLinkRequest,
  playerDeepLinkRequestVar,
} from "../utils/playerDeepLinkRequests";

/** Arguments for {@link usePlayerDeepLink}. */
interface UsePlayerDeepLinkArgs {
  /**
   * Cards on the ACTIVE board, in the same shape the add-players panel
   * consumes. The hook only opens the drawer for a player who actually has a
   * card here — a stale/removed player degrades to the plain board landing,
   * never a broken drawer (the SE-7121 graceful-fallback AC).
   */
  readonly kanbanCards: readonly { player: { id: string } }[];
  /**
   * True once the active board's columns have loaded. The param is held
   * until then: the drawer needs the board's card data before it can decide
   * whether the player is present — the same wait-for-data sequencing
   * `useBoardDeepLink` applies to the board list.
   */
  readonly boardReady: boolean;
  /**
   * The active board's id, used to hold an in-app deep-link request that
   * targets a DIFFERENT board until that board has actually become active —
   * otherwise the request would be judged against the outgoing board's card
   * pool and silently consumed (SE-7145).
   */
  readonly activeBoardId?: string;
  /** Opens the preview drawer for a player id (`usePlayerSidebar`'s setter). */
  readonly openPlayer: (playerId: string) => void;
}

/**
 * Drives the player preview drawer from pipeline deep links. Two paths:
 *
 * 1. **In-app clicks** (SE-7145): the newsfeed hands its intent over through
 *    `requestPlayerDeepLink` — each click is a fresh token consumed at most
 *    once, so clicking the same player twice re-opens the drawer and Expo
 *    Router's web param-stripping cannot lose the navigation. The screen
 *    stays mounted across navigations, which is exactly why a value-equality
 *    latch on the URL param was not enough.
 * 2. **Arrivals** (full reload / shared URL): live param first, then the
 *    `readInitialRouteParam` seed (Expo Router on web strips unrecognized
 *    params during hydration), applied once per param value via a ref so it
 *    never re-opens a drawer the user has closed.
 * @param args - Cards, readiness, board identity, and the drawer-open action.
 * @param args.kanbanCards - Cards on the active board.
 * @param args.boardReady - True once the board's columns have loaded.
 * @param args.activeBoardId - The active board's id.
 * @param args.openPlayer - Opens the preview drawer for a player id.
 * @returns Nothing; the effect opens the drawer as a side effect.
 */
export function usePlayerDeepLink({
  kanbanCards,
  boardReady,
  activeBoardId,
  openPlayer,
}: UsePlayerDeepLinkArgs): void {
  const { playerId: livePlayerId } = useLocalSearchParams<{
    playerId?: string;
  }>();

  // Captured once, same rationale as useBoardDeepLink's seed: the arrival
  // URL is the intent, and re-reading each render would observe later state.
  const [seedPlayerId] = useState<string | undefined>(() =>
    readInitialRouteParam(PLAYER_KANBAN_ROUTE_KEY, PLAYER_ID_PARAM)
  );

  // Each in-app click is a fresh request consumed exactly once (the store
  // clears on consume) — value equality deliberately plays no part, so the
  // same player can be requested again and a consumed request can never
  // replay against a later remount.
  const request = useReactiveVar(playerDeepLinkRequestVar);

  // Applied-once guard for the ARRIVAL path: reacts only to a genuine param
  // change, so closing the drawer stays closed and manual selection is never
  // stomped.
  const appliedPlayerIdRef = useRef<string | undefined>(undefined);

  const arrivalPlayerId = livePlayerId ?? seedPlayerId;
  const playerId = request?.playerId ?? arrivalPlayerId;

  const hasCard = kanbanCards.some(card => card.player.id === playerId);
  const hasCards = kanbanCards.length > 0;
  const requestToken = request?.token;
  const requestBoardId = request?.boardId;

  useEffect(() => {
    if (!boardReady) {
      return;
    }
    // Columns hydrate LAZILY, so `boardReady` can flip true while the card
    // pool is still empty — latching on that first empty evaluation is how
    // the initial ship missed every deep link (the staging repro). The param
    // therefore stays pending until cards actually exist; only then is a
    // miss a genuine miss (the graceful fallback: land on the board, no
    // drawer) and safe to latch so a player added minutes later cannot
    // surprise-open a drawer long after arrival.
    if (!hasCards) {
      return;
    }
    if (requestToken !== undefined) {
      // A board-targeted click is held until its board is the active one, so
      // it cannot be judged (and consumed) against the outgoing board.
      if (
        requestBoardId !== undefined &&
        activeBoardId !== undefined &&
        requestBoardId !== activeBoardId
      ) {
        return;
      }
      consumePlayerDeepLinkRequest(requestToken);
      // Keep the arrival latch in sync so the URL path cannot re-fire for
      // the same navigation after the request was already applied.
      appliedPlayerIdRef.current = playerId;
      if (hasCard && playerId !== undefined) {
        openPlayer(playerId);
      }
      return;
    }
    if (!playerId) {
      return;
    }
    if (appliedPlayerIdRef.current === playerId) {
      return;
    }
    appliedPlayerIdRef.current = playerId;
    if (hasCard) {
      openPlayer(playerId);
    }
  }, [
    activeBoardId,
    boardReady,
    hasCard,
    hasCards,
    openPlayer,
    playerId,
    requestBoardId,
    requestToken,
  ]);
}
